import './GalleryBulkUpload.css';
import {
    Form,
    Input,
    Button,
    Upload,
    Row,
    Col,
    Typography,
    Space,
} from 'antd';
import { toast } from 'react-toastify';
import {
    InboxOutlined, PictureOutlined, CloseCircleOutlined,
    PlusOutlined, EditOutlined, DeleteOutlined
} from '@ant-design/icons';
import {
    createGalleryItem, updateGalleryItem, addImagesToGalleryFolder,
    updateGalleryImageOrder, bulkDeleteGalleryImages
} from '../../services/galleryApi';
import React, { useState, useMemo, useCallback, useEffect } from 'react';

const MAX_SIZE_MB = 5;
const { Dragger } = Upload;

const reorderFiles = (list, fromIndex, toIndex) => {
    const updated = [...list];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    return updated;
};

const revokePreview = (file) => {
    if (file?.preview && file.preview.startsWith('blob:')) {
        URL.revokeObjectURL(file.preview);
    }
};

const extractName = (url) => {
    try {
        return url.split('/').pop()?.split('?')[0]
    } catch {
        return 'image';
    }
};

const GalleryGrid = React.memo(
    ({ fileList, dragIndex, onDragStart, onDragEnter, onDragEnd, onRemove, draggerProps, uploading }) => (
        <div className="bulk-upload-gallery">
            <Dragger
                {...draggerProps}
                disabled={uploading}
                className="bulk-upload-dragger slim"
                fileList={[]}
            >
                <InboxOutlined style={{ fontSize: fileList.length === 1 ? 40 : 30, color: '#6f2dff' }} />
                <p style={{ fontSize: 11 }} className="ant-upload-text">
                    Drag image files to upload
                </p>
            </Dragger>
            {fileList.map((file, index) => {
                const thumbnail = file.preview || file.url;
                return (
                    <div
                        draggable
                        onDragEnd={onDragEnd}
                        key={file.uid || file.name || index}
                        onDragStart={() => onDragStart(index)}
                        onDragEnter={() => onDragEnter(index)}
                        onDragOver={(e) => e.preventDefault()}
                        className={`gallery-card ${dragIndex === index ? 'dragging' : ''}`}
                    >
                        <div className="gallery-thumb">
                            {thumbnail ? (
                                <img src={thumbnail} alt={file.name} loading="lazy" style={{ objectFit: "cover" }} />
                            ) : (
                                <PictureOutlined className="gallery-thumb-placeholder" />
                            )}
                            <span className="gallery-order-pill">{index + 1}</span>
                        </div>
                        <button
                            type="button"
                            className="gallery-remove"
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemove(file);
                            }}
                        >
                            <CloseCircleOutlined />
                        </button>
                    </div>
                );
            })}
        </div>
    )
);

const GalleryBulkUpload = ({ onSuccess, onCancel, initialValues }) => {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);
    const [dragIndex, setDragIndex] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [coverFileList, setCoverFileList] = useState([]);
    const [deletedImageIds, setDeletedImageIds] = useState([]);
    const hasFiles = fileList.length > 0;

    const handleSubmit = async (values) => {
        if (!fileList.length && deletedImageIds.length === 0) {
            toast.error('Please select at least one image or remove images to submit');
            return;
        }

        setUploading(true);
        try {
            let folderId = initialValues?.id;

            // PHASE 0: Handle Bulk Deletions
            if (deletedImageIds.length > 0 && folderId) {
                await bulkDeleteGalleryImages({ imageIds: deletedImageIds });
            }

            // Detect Changes for Phase 1
            const isNew = !folderId;
            const metadataChanged = isNew ||
                values.category !== (initialValues.folderName || '') ||
                (values.description || '') !== (initialValues.description || '') ||
                values.titlePrefix !== (initialValues.title || '');

            const coverChanged = coverFileList.length > 0 && !!coverFileList[0].originFileObj;

            // PHASE 1: Create or Update the Folder Metadata
            if (isNew || metadataChanged || coverChanged) {
                const folderFormData = new FormData();
                folderFormData.append('folderName', values.category);
                folderFormData.append('description', values.description || '');
                folderFormData.append('title', values.titlePrefix?.trim() ? `${values.titlePrefix.trim()}` : '');

                if (coverFileList.length > 0 && coverFileList[0].originFileObj) {
                    folderFormData.append('image', coverFileList[0].originFileObj);
                }

                if (isNew) {
                    const folderResponse = await createGalleryItem(folderFormData);
                    folderId = folderResponse.id;
                } else {
                    await updateGalleryItem(folderId, folderFormData);
                }
            }

            if (!folderId) {
                throw new Error('Failed to obtain Folder ID');
            }

            // PHASE 2: Upload New Images
            const newFiles = fileList.filter(file => !!file.originFileObj);
            let newlyUploadedImages = [];

            if (newFiles.length > 0) {
                const imagesFormData = new FormData();
                newFiles.forEach((file) => {
                    imagesFormData.append('images', file.originFileObj);
                });

                const uploadResponse = await addImagesToGalleryFolder(folderId, imagesFormData);
                newlyUploadedImages = uploadResponse.images || [];
            }

            // PHASE 3: Update Image Order
            // Calculate if order has changed or new images were added
            const initialOrderIds = (initialValues?.images || []).map(img => typeof img === 'object' ? img.id : null).filter(id => id);
            const currentOrderIds = fileList.map(file => file.imageId).filter(id => id);

            const orderChanged = JSON.stringify(initialOrderIds) !== JSON.stringify(currentOrderIds);

            if (isNew || newFiles.length > 0 || orderChanged) {
                let newImageIdx = 0;
                const imageOrders = fileList.map((file, index) => {
                    let id = file.imageId;
                    if (!id && file.originFileObj) {
                        id = newlyUploadedImages[newImageIdx++]?.id;
                    }
                    return { id, order: index };
                }).filter(item => item.id);

                if (imageOrders.length > 0) {
                    await updateGalleryImageOrder({ imageOrders });
                }
            }

            toast.success(isNew ? 'Folder created successfully' : 'Folder updated successfully');
            form.resetFields();
            fileList.forEach(revokePreview);
            setFileList([]);
            setDeletedImageIds([]);
            onSuccess?.();
        } catch (error) {
            console.error('Submit Error:', error);
            toast.error(error.response?.data?.message || error.message || 'Failed to save gallery folder');
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = useCallback((targetFile) => {
        setFileList((prev) => {
            const existing = prev.find((item) => item.uid === targetFile.uid);
            if (existing) {
                revokePreview(existing);
                // Track deleted images for Phase 0
                if (existing.imageId) {
                    setDeletedImageIds(prevIds => [...prevIds, existing.imageId]);
                }
            }
            return prev.filter((item) => item.uid !== targetFile.uid);
        });
    }, []);

    const draggerProps = useMemo(
        () => ({
            multiple: true,
            accept: 'image/*',
            fileList,
            beforeUpload: (file) => {
                if (!file.type.startsWith('image/')) {
                    toast.error('You can only upload image files!');
                    return Upload.LIST_IGNORE;
                }
                const isLt5M = file.size / 1024 / 1024 < MAX_SIZE_MB;
                if (!isLt5M) {
                    toast.error(`Each image must be smaller than ${MAX_SIZE_MB}MB!`);
                    return Upload.LIST_IGNORE;
                }
                const previewUrl = URL.createObjectURL(file);
                const normalizedFile = {
                    ...file,
                    status: 'done',
                    preview: previewUrl,
                    originFileObj: file,
                };
                setFileList((prev) => [...prev, normalizedFile]);
                return false;
            },
            onRemove: (file) => {
                handleRemove(file);
            },
            showUploadList: false,
        }),
        [fileList, handleRemove]
    );

    const coverUploadProps = {
        showUploadList: false,
        maxCount: 1,
        accept: 'image/*',
        fileList: coverFileList,
        beforeUpload: (file) => {
            const isLt5M = file.size / 1024 / 1024 < MAX_SIZE_MB;
            if (!isLt5M) {
                toast.error(`Image must be smaller than ${MAX_SIZE_MB}MB!`);
                return Upload.LIST_IGNORE;
            }
            const previewUrl = URL.createObjectURL(file);
            setCoverFileList([{
                ...file,
                status: 'done',
                url: previewUrl,
                originFileObj: file,
            }]);
            return false;
        },
    };

    const handleDragStart = useCallback((index) => {
        setDragIndex(index);
    }, []);

    const handleDragEnter = useCallback(
        (index) => {
            if (dragIndex === null || dragIndex === index) return;
            setFileList((prev) => reorderFiles(prev, dragIndex, index));
            setDragIndex(index);
        },
        [dragIndex]
    );

    const handleDragEnd = useCallback(() => {
        setDragIndex(null);
    }, []);

    const memoizedGrid = useMemo(() => hasFiles && (
        <GalleryGrid
            fileList={fileList}
            dragIndex={dragIndex}
            onDragStart={handleDragStart}
            onDragEnter={handleDragEnter}
            onDragEnd={handleDragEnd}
            onRemove={handleRemove}
            draggerProps={draggerProps}
            uploading={uploading}
        />
    ), [hasFiles, fileList, dragIndex, handleDragStart, handleDragEnter, handleDragEnd, handleRemove, draggerProps, uploading]);

    useEffect(() => {
        if (initialValues?.id) {
            form.setFieldsValue({
                category: initialValues.folderName || '',
                description: initialValues.description || '',
                titlePrefix: initialValues.title || '',
                eventId: initialValues.eventId || undefined,
            });

            const initialImages = (initialValues?.images || [])?.sort((a, b) => a.order - b.order).map((img, index) => {
                const isObject = typeof img === 'object' && img !== null;
                const url = isObject ? img.imageUrl : img;
                const id = isObject ? img.id : null;

                return {
                    url: url,
                    status: 'done',
                    name: extractName(url),
                    uid: id ? `id-${id}` : `initial-${index}`,
                    imageId: id,
                };
            });

            setFileList(initialImages);

            if (initialValues.coverImage) {
                setCoverFileList([{
                    uid: '-cover',
                    name: 'Cover Image',
                    status: 'done',
                    url: initialValues.coverImage,
                }]);
            }
        } else {
            form.resetFields();
            setFileList([]);
            setCoverFileList([]);
            setDeletedImageIds([]);
        }
    }, [initialValues, form]);

    return (
        <Form
            form={form}
            layout="vertical"
            disabled={uploading}
            onFinish={handleSubmit}
            className="bulk-upload-form"
        >
            <div className="bulk-upload-container">

                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="category"
                            label="Folder Name"
                            rules={[{ required: true, message: 'Please enter a folder name' }]}
                        >
                            <Input placeholder="e.g. Annual Day 2024" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="titlePrefix"
                            label="Title Prefix"
                        >
                            <Input placeholder="e.g. Annual Day" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col xs={24}>
                        <Form.Item name="description" label="Description">
                            <Input.TextArea
                                rows={3}
                                placeholder="Optional description applied to every image"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    label="Folder Cover Image"
                    tooltip="This image will be used as the thumbnail for the entire category"
                    style={{ marginBottom: 20 }}
                >
                    <div className="cover-upload-wrapper">
                        <Upload {...coverUploadProps}>
                            <div className={`cover-upload-card ${coverFileList.length > 0 ? 'has-image' : ''}`}>
                                {coverFileList.length > 0 ? (
                                    <div className="cover-preview">
                                        <img src={coverFileList[0].url} alt="Cover Preview" />
                                        <div className="cover-preview-overlay">
                                            <div className="cover-action-btn">
                                                <EditOutlined /> Change
                                            </div>
                                            <div
                                                className="cover-action-btn remove"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (coverFileList[0]?.url?.startsWith('blob:')) {
                                                        URL.revokeObjectURL(coverFileList[0].url);
                                                    }
                                                    setCoverFileList([]);
                                                }}
                                            >
                                                <DeleteOutlined /> Remove
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="cover-placeholder">
                                        <PlusOutlined />
                                        <span>Upload Cover Photo</span>
                                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                                            Recommended: 16:9 aspect ratio
                                        </Typography.Text>
                                    </div>
                                )}
                            </div>
                        </Upload>
                    </div>
                </Form.Item>


                <Form.Item
                    required
                    label={"Images"}
                    style={{ height: fileList?.length === 0 ? null : 25 }}
                >
                    {fileList?.length === 0 ? (
                        <Dragger
                            {...draggerProps}
                            disabled={uploading}
                            className="bulk-upload-dragger"
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">Click or drag image files to upload</p>
                            <p className="ant-upload-hint">
                                Supported formats: JPG, PNG, GIF. Max size {MAX_SIZE_MB}MB each.
                            </p>
                        </Dragger>
                    ) : <></>}
                </Form.Item>

                {memoizedGrid}

                {fileList?.length > 1 && (
                    <Typography.Text
                        style={{
                            margin: '20px',
                            display: 'block',
                            textAlign: 'center',
                            color: '#6b7280',
                        }}
                    >
                        — Drag images to reorder —
                    </Typography.Text>
                )}
            </div>

            <Space className="bulk-upload-actions">
                <Button onClick={onCancel}>Cancel</Button>
                <Button
                    type="primary"
                    htmlType="submit"
                    loading={uploading}
                    disabled={!fileList.length}
                    style={{
                        backgroundColor: '#6f2dff',
                        borderColor: '#6f2dff',
                        color: 'white',
                    }}
                >
                    {uploading ? 'Uploading…' : 'Upload Images'}
                </Button>
            </Space>
        </Form>
    );
}

export default GalleryBulkUpload;
